import { useState, useEffect, useRef, useCallback } from 'react';
import { useParticipants } from '@livekit/components-react';
import { setVisibilityAwareInterval } from '../../../utils/VisibilityAwareTimer';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores';
import { getPhaserGameSync } from '../../../PhaserGame';
import Game from '../../../scenes/Game';
import { ProximityState } from '../types/LiveKitVideoDisplay.types';
import { PLAYER_PROXIMITY_CHANGED } from '../styles/LiveKitVideoDisplay.styles';
import { canPlayersCommunicate } from '../../../services/VirtualRoomProximityHelper';
import { DEFAULT_PROXIMITY_FALLBACK_PX } from '../../../constants/proximity';
import { virtualRoomAreaService } from '../../../services/VirtualRoomAreaService';
import { getPlayerPositionForMediaProximity } from '../../../services/getPlayerPositionForMediaProximity';
import { phaserEvents, Event } from '../../../events/EventCenter';
import { canUseProximityMemo } from '../utils/proximityMemoPolicy';
import { virtualRoomAreaDetectionService } from '../../../services/VirtualRoomAreaDetectionService';
import { hasServerVirtualRoomCoOccupancy, getLocalServerVirtualRoomId, getRemoteServerVirtualRoomId, shouldExcludeByServerVirtualRoom } from '../utils/resolveSameVirtualRoomMedia';

// Follow / Escort: session IDs that should always be treated as in proximity
// because the local player is following them or they are following us.
const followProximityOverrides = new Set<string>();

// Performance optimization: Cache for player objects to avoid recreation
const playerObjectCache = new Map<string, { x: number; y: number; playerId: string }>();

// Performance monitoring
const performanceMetrics = {
  calculationCount: 0,
  lastCalculationTime: 0,
  averageCalculationTime: 0,
  throttledCount: 0,
};

// Debug function to log performance metrics
const logPerformanceMetrics = () => {
  if (performanceMetrics.calculationCount > 0) {

  }
};

// Expose performance monitoring globally for debugging
if (typeof window !== 'undefined') {
  (window as any).logProximityPerformance = logPerformanceMetrics;
}

// Debug function to check VirtualRoomAreaService status
const debugVirtualRoomAreaService = () => {
  const phaserGame = getPhaserGameSync();
  if (!phaserGame) return;

  // Check if myPlayer exists and get their area
  const game = phaserGame.scene?.keys?.game as Game | undefined;
  if (game && game.myPlayer) {
    const myArea = virtualRoomAreaService.isPlayerInArea(game.myPlayer.x, game.myPlayer.y);

  }
};

// Expose debug function globally
if (typeof window !== 'undefined') {
  (window as any).debugVirtualRoomAreaService = debugVirtualRoomAreaService;
}

// Efficient set comparison without JSON.stringify
function areSetsEqual(set1: Set<string>, set2: Set<string>): boolean {
  if (set1.size !== set2.size) return false;
  for (const item of set1) {
    if (!set2.has(item)) return false;
  }
  return true;
}

// How long (ms) to include a new LiveKit participant before any position data
// has arrived, so the first few frames don't "fail closed" and cause one-way media.
const SETTLE_GRACE_MS = 3000;

// Lower speaking sensitivity: require higher level and shorter tail to reduce false "speaking" from noise
const SPEAKING_AUDIO_LEVEL_THRESHOLD = 0.25;
const SPEAKING_LAST_SPOKE_MS = 500;
const SPEAKING_UPDATE_INTERVAL_MS = 200;

export const useProximityState = (): ProximityState => {
  const participants = useParticipants();
  const [speakingIds, setSpeakingIds] = useState<Set<string>>(new Set());
  const [lastSpeakingTimes, setLastSpeakingTimes] = useState<Map<string, number>>(new Map());
  const [speakingOrder, setSpeakingOrder] = useState<string[]>([]);
  const [inProximityIds, setInProximityIds] = useState<Set<string>>(new Set());
  const updateProximityImmediateRef = useRef<(() => void) | null>(null);

  const gameRef = useRef<Game | null>(null);
  const isLoading = useSelector((state: RootState) => state.game.loadingGame);
  const virtualRoomFilters = useSelector((state: RootState) => state.virtualRoomFilter.filters);

  // Performance optimization: Track last player positions to avoid unnecessary recalculations
  const lastPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const lastUpdateTimeRef = useRef<number>(0);
  const isMovingRef = useRef<boolean>(false);
  const lastProximityResultRef = useRef<Set<string>>(new Set());
  const lastParticipantsSizeRef = useRef<number>(0);
  const lastSpeakingTimesRef = useRef<Map<string, number>>(new Map());
  const speakingOrderRef = useRef<string[]>([]);
  // Hysteresis: require N consecutive misses before removing a participant from proximity
  const consecutiveMissCountRef = useRef<Map<string, number>>(new Map());
  /** Dev-only: throttle "no position for LiveKit remote" logs */
  const proximityPositionMissLogAtRef = useRef<Map<string, number>>(new Map());
  /** First-seen timestamp per participant for settle grace window. */
  const firstSeenRef = useRef<Map<string, number>>(new Map());

  // Initialize game ref — poll until Phaser is available (may not be ready on mount).
  useEffect(() => {
    const trySetGameRef = () => {
      const phaserGame = getPhaserGameSync();
      if (phaserGame) {
        gameRef.current = phaserGame.scene?.keys?.game as Game | undefined;
      }
    };
    trySetGameRef();
    // If it was null on mount, keep trying every 200ms.
    if (!gameRef.current) {
      const interval = setInterval(() => {
        trySetGameRef();
        if (gameRef.current) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);


  // Follow / Escort: keep follow-paired session IDs in proximity regardless of distance.
  useEffect(() => {
    const handleFollowStateChanged = (data: { following: boolean; targetId?: string }) => {
      if (data.following && data.targetId) {
        followProximityOverrides.add(data.targetId);
      } else if (data.targetId) {
        followProximityOverrides.delete(data.targetId);
      }
    };
    const beingFollowedIds = new Set<string>();
    const handleBeingFollowedChanged = (data: { beingFollowed: boolean; followerSessionId?: string }) => {
      if (data.beingFollowed && data.followerSessionId) {
        beingFollowedIds.add(data.followerSessionId);
        followProximityOverrides.add(data.followerSessionId);
      } else if (data.followerSessionId) {
        beingFollowedIds.delete(data.followerSessionId);
        followProximityOverrides.delete(data.followerSessionId);
      } else {
        // No specific follower — remove only being-followed overrides,
        // preserving any active follow override (where we follow someone).
        for (const id of beingFollowedIds) {
          followProximityOverrides.delete(id);
        }
        beingFollowedIds.clear();
      }
    };
    phaserEvents.on(Event.FOLLOW_STATE_CHANGED, handleFollowStateChanged);
    phaserEvents.on(Event.BEING_FOLLOWED_STATE_CHANGED, handleBeingFollowedChanged);
    return () => {
      phaserEvents.off(Event.FOLLOW_STATE_CHANGED, handleFollowStateChanged);
      phaserEvents.off(Event.BEING_FOLLOWED_STATE_CHANGED, handleBeingFollowedChanged);
      followProximityOverrides.clear();
    };
  }, []);

  // Debug function to force area detection and proximity recalculation
  const forceProximityRecalculation = useCallback(() => {

    // Force area detection for all players
    const game = gameRef.current;
    if (game && game.myPlayer) {
      // Check if player is registered before updating position
      if (virtualRoomAreaDetectionService.isPlayerRegistered(game.myPlayer.playerId)) {
        virtualRoomAreaDetectionService.updatePlayerPosition(game.myPlayer.playerId, game.myPlayer.x, game.myPlayer.y);
      }

      // Trigger area detection for other players
      participants.forEach((participant) => {
        if (!participant.isLocal) {
          const pos = getPlayerPositionForMediaProximity(game, participant.identity);
          if (pos && virtualRoomAreaDetectionService.isPlayerRegistered(pos.playerId)) {
            virtualRoomAreaDetectionService.updatePlayerPosition(pos.playerId, pos.x, pos.y);
          }
        }
      });
    }

    // Invalidate the memo so calculateProximity does a real pass — without
    // this, "force" is advisory and can return a result up to 1s old.
    lastUpdateTimeRef.current = 0;
    const newProximityIds = calculateProximity();
    if (newProximityIds) {
      setInProximityIds(newProximityIds);
    }
  }, [participants]);

  // Expose force recalculation function globally
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).forceProximityRecalculation = forceProximityRecalculation;
    }
  }, [forceProximityRecalculation]);

  // Wire the `force-proximity-recalculation` custom event so retry events
  // from ParticipantVideo and LiveKitSafetyChecker trigger an actual recalc.
  useEffect(() => {
    const handler = () => forceProximityRecalculation();
    window.addEventListener('force-proximity-recalculation', handler);
    return () => window.removeEventListener('force-proximity-recalculation', handler);
  }, [forceProximityRecalculation]);

  // SC-304: invalidate the position cache when the tab regains focus so that
  // calculateProximity does a full recalculation instead of returning stale
  // cached results from the hidden period (where Phaser sprites were frozen
  // and positions may not have updated even though Colyseus schema did).
  useEffect(() => {
    const handlePageVisible = () => {
      lastUpdateTimeRef.current = 0;
      lastPositionsRef.current.clear();
      lastProximityResultRef.current = new Set();
      consecutiveMissCountRef.current.clear();
    };
    window.addEventListener('page-visible', handlePageVisible);
    return () => window.removeEventListener('page-visible', handlePageVisible);
  }, []);

  // SC-304: when a player's Colyseus session is removed, immediately purge
  // all per-player caches and force-remove them from proximity. Without this,
  // the 3-miss hysteresis keeps departed players in proximity for extra cycles,
  // and the 90-second lastKnownPlayerPosition cache can provide stale positions.
  useEffect(() => {
    const handlePlayerLeft = (playerId: string) => {
      // Force the next calculateProximity() to do a real pass rather than
      // returning the <1s memo, which still contains this player.
      lastUpdateTimeRef.current = 0;
      lastProximityResultRef.current.delete(playerId);

      lastPositionsRef.current.delete(playerId);
      consecutiveMissCountRef.current.delete(playerId);
      firstSeenRef.current.delete(playerId);
      proximityPositionMissLogAtRef.current.delete(playerId);
      // Bypass hysteresis: immediately remove from proximity set
      setInProximityIds((prev) => {
        if (!prev.has(playerId)) return prev;
        const next = new Set(prev);
        next.delete(playerId);
        return next;
      });
    };
    phaserEvents.on(Event.PLAYER_LEFT, handlePlayerLeft);
    return () => { phaserEvents.off(Event.PLAYER_LEFT, handlePlayerLeft); };
  }, []);

  // Optimized proximity calculation with instant responsiveness
  const calculateProximity = useCallback(() => {
    // Check if proximity systems are disabled for testing
    if (typeof window !== 'undefined' && (window as any).disableProximityCalculations) {
      return new Set<string>();
    }

    const startTime = performance.now();
    performanceMetrics.calculationCount++;

    // Safety: re-read game ref in case it was null on mount but is now available.
    if (!gameRef.current) {
      const phaserGame = getPhaserGameSync();
      if (phaserGame) {
        gameRef.current = phaserGame.scene?.keys?.game as Game | undefined;
      }
    }
    const game = gameRef.current;
    if (!game || !game.myPlayer) return new Set<string>();

    const currentTime = Date.now();
    const myPlayer = game.myPlayer;
    const currentPos = { x: myPlayer.x, y: myPlayer.y };
    const lastPos = lastPositionsRef.current.get('myPlayer');

    // Check if myPlayer has moved (significant position change > 2px)
    let anyoneMoved = !lastPos ||
      Math.abs(currentPos.x - lastPos.x) > 2 ||
      Math.abs(currentPos.y - lastPos.y) > 2;

    // Check if any remote player has moved, or if we have new/removed participants
    if (!anyoneMoved) {
      for (const participant of participants) {
        if (participant.isLocal) continue;
        const otherPos = getPlayerPositionForMediaProximity(game, participant.identity);
        if (otherPos) {
          const lastOtherPos = lastPositionsRef.current.get(participant.identity);
          if (!lastOtherPos ||
            Math.abs(otherPos.x - lastOtherPos.x) > 2 ||
            Math.abs(otherPos.y - lastOtherPos.y) > 2) {
            anyoneMoved = true;
            break;
          }
        } else {
          // If a participant has no position, but they used to have one (or vice versa), mark as moved
          if (lastPositionsRef.current.has(participant.identity)) {
            anyoneMoved = true;
            break;
          }
        }
      }
    }

    // Check if participants size has changed
    if (participants.length !== lastParticipantsSizeRef.current) {
      anyoneMoved = true;
    }

    if (canUseProximityMemo({
      anyoneMoved,
      msSinceLastFullCalc: currentTime - lastUpdateTimeRef.current,
      hasCachedResult: lastProximityResultRef.current.size !== 0,
    })) {
      return lastProximityResultRef.current;
    }

    // Update positions cache for next time
    lastPositionsRef.current.set('myPlayer', currentPos);
    participants.forEach((participant) => {
      if (participant.isLocal) return;
      const otherPos = getPlayerPositionForMediaProximity(game, participant.identity);
      if (otherPos) {
        lastPositionsRef.current.set(participant.identity, { x: otherPos.x, y: otherPos.y });
      } else {
        lastPositionsRef.current.delete(participant.identity);
      }
    });

    lastUpdateTimeRef.current = currentTime;
    lastParticipantsSizeRef.current = participants.length;
    const newProximityIds = new Set<string>();

    // Bug 5 fix: Periodic cleanup of playerObjectCache to prevent memory leak
    // Remove entries for players no longer in the participant list
    if (playerObjectCache.size > participants.length + 10) {
      const currentPlayerIds = new Set(participants.map(p => {
        const pos = getPlayerPositionForMediaProximity(game, p.identity);
        return pos?.playerId;
      }).filter(Boolean));
      playerObjectCache.forEach((_, key) => {
        if (!currentPlayerIds.has(key)) {
          playerObjectCache.delete(key);
        }
      });
    }

    // Prune stale first-seen entries for participants that have left.
    if (firstSeenRef.current.size > participants.length + 10) {
      const currentIdentities = new Set(participants.map(p => p.identity));
      firstSeenRef.current.forEach((_, key) => {
        if (!currentIdentities.has(key)) firstSeenRef.current.delete(key);
      });
    }

    // Resolve server-authoritative virtual room for the local player once per cycle.
    const mySid = game.network?.mySessionId || '';
    const myServerVr = getLocalServerVirtualRoomId(game);

    const now = Date.now();

    // Determine whether local player is inside a password-protected room.
    // Grace-window "include" must NEVER bypass password gates.
    const myArea = virtualRoomAreaService.isPlayerInArea(myPlayer.x, myPlayer.y);
    const myRoomFilter = myArea ? virtualRoomFilters[myArea.roomId] : undefined;
    const inPasswordRoom = !!(myRoomFilter?.hasPassword);

    // Always include local player
    participants.forEach((participant) => {
      if (participant.isLocal) {
        newProximityIds.add(participant.identity);
      } else {
        // Track first-seen time for the settle grace window.
        if (!firstSeenRef.current.has(participant.identity)) {
          firstSeenRef.current.set(participant.identity, now);
        }

        // Follow / Escort: always keep follow-paired participants in proximity
        if (followProximityOverrides.has(participant.identity)) {
          newProximityIds.add(participant.identity);
          return;
        }

        // Server/broadcast VR co-occupancy before position — large VR sessions must not
        // drop peers when sprite/position sync lags behind LiveKit participant list.
        if (hasServerVirtualRoomCoOccupancy(game, participant.identity, virtualRoomFilters)) {
          newProximityIds.add(participant.identity);
          return;
        }

        // Server-enforced isolation: if the server definitively states they are in DIFFERENT virtual room contexts
        // (including one being inside and the other outside), they must be isolated. This prevents edge-case
        // proximity leaks where a person standing just outside the VR client-side boundary sees someone inside.
        // SC-273 AC3: only hard-exclude on a DEFINITIVE mismatch (our own server VR is known). On a fresh join,
        // before the server syncs our own VR id back, our server VR is null and every existing occupant would
        // otherwise be excluded here — starving the new joiner of inbound audio for the first minutes. When our
        // server VR is unknown, defer to the password-gated client-side area detection below (which still enforces
        // the room password, so a locked room cannot leak).
        const otherServerVr = getRemoteServerVirtualRoomId(game, participant.identity);
        if (shouldExcludeByServerVirtualRoom(myServerVr, otherServerVr)) {
          return;
        }


        const otherPos = getPlayerPositionForMediaProximity(game, participant.identity);
        if (!otherPos) {
          // Grace window: include new participants briefly so tracks are not
          // prematurely unsubscribed while position data is still in-flight.
          // Security: skip grace when in a password-protected room.
          const firstSeen = firstSeenRef.current.get(participant.identity) ?? now;
          if (!inPasswordRoom && now - firstSeen < SETTLE_GRACE_MS) {
            newProximityIds.add(participant.identity);
            return;
          }
          if (process.env.NODE_ENV === 'development') {
            const last = proximityPositionMissLogAtRef.current.get(participant.identity) ?? 0;
            if (now - last > 8000) {
              proximityPositionMissLogAtRef.current.set(participant.identity, now);
              console.warn(
                '[Proximity] Excluded (no position):',
                participant.identity
              );
            }
          }
          return;
        }

        // Use cached player objects to avoid recreation
        const otherPlayerKey = otherPos.playerId;
        let otherPlayerObj = playerObjectCache.get(otherPlayerKey);

        if (!otherPlayerObj ||
          otherPlayerObj.x !== otherPos.x ||
          otherPlayerObj.y !== otherPos.y) {
          otherPlayerObj = {
            x: otherPos.x,
            y: otherPos.y,
            playerId: otherPos.playerId,
          };
          playerObjectCache.set(otherPlayerKey, otherPlayerObj);
        }

        // Client-side area detection fallback (myArea is resolved once above)
        const otherArea = virtualRoomAreaService.isPlayerInArea(otherPlayerObj.x, otherPlayerObj.y);

        // Room isolation: different client-side rooms → exclude
        if (myArea && otherArea && myArea.roomId !== otherArea.roomId) {
          if (process.env.NODE_ENV === 'development') {
            const now = Date.now();
            const last = proximityPositionMissLogAtRef.current.get(participant.identity) ?? 0;
            if (now - last > 8000) {
              proximityPositionMissLogAtRef.current.set(participant.identity, now);
              console.warn(
                '[Proximity] Excluded (different rooms):',
                participant.identity,
                myArea.roomId,
                'vs',
                otherArea.roomId
              );
            }
          }
          return;
        }

        // One in a room, the other not → exclude
        if ((myArea && !otherArea) || (!myArea && otherArea)) {
          if (process.env.NODE_ENV === 'development') {
            const now = Date.now();
            const last = proximityPositionMissLogAtRef.current.get(participant.identity) ?? 0;
            if (now - last > 8000) {
              proximityPositionMissLogAtRef.current.set(participant.identity, now);
              console.warn(
                '[Proximity] Excluded (mixed room context):',
                participant.identity,
                'myArea:', !!myArea,
                'otherArea:', !!otherArea
              );
            }
          }
          return;
        }

        // Same client-resolved virtual room: include for media regardless of distance (large rooms, N people).
        if (myArea && otherArea && myArea.roomId === otherArea.roomId) {
          const roomFilter = virtualRoomFilters[myArea.roomId];
          const passwordOk =
            !roomFilter?.hasPassword ||
            (roomFilter.authorizedSessionIds.includes(game.network?.mySessionId || '') &&
              roomFilter.authorizedSessionIds.includes(participant.identity));
          if (passwordOk) {
            newProximityIds.add(participant.identity);
            return;
          }
          return;
        }

        let fallbackDistance = DEFAULT_PROXIMITY_FALLBACK_PX;

        const shouldConnect = canPlayersCommunicate(
          { x: myPlayer.x, y: myPlayer.y, playerId: myPlayer.playerId },
          otherPlayerObj,
          fallbackDistance
        );

        if (shouldConnect) {
          newProximityIds.add(participant.identity);
        } else if (process.env.NODE_ENV === 'development') {
          const now = Date.now();
          const last = proximityPositionMissLogAtRef.current.get(participant.identity) ?? 0;
          if (now - last > 8000) {
            proximityPositionMissLogAtRef.current.set(participant.identity, now);
            console.warn(
              '[Proximity] Excluded (distance):',
              participant.identity,
              'fallback:', fallbackDistance
            );
          }
        }
      }
    });

    // Update performance metrics
    const calculationTime = performance.now() - startTime;
    performanceMetrics.lastCalculationTime = calculationTime;
    performanceMetrics.averageCalculationTime =
      (performanceMetrics.averageCalculationTime * (performanceMetrics.calculationCount - 1) + calculationTime) / performanceMetrics.calculationCount;

    lastProximityResultRef.current = newProximityIds;
    return newProximityIds;
  }, [participants, virtualRoomFilters]);

  // Speaking detection logic (optimized)
  useEffect(() => {
    const updateSpeaking = () => {
      const newSpeakingIds = new Set<string>();
      const currentTime = Date.now();
      let speakingTimesChanged = false;
      let speakingOrderChanged = false;

      // Accumulate changes locally
      const nextSpeakingTimes = new Map(lastSpeakingTimesRef.current);
      const nextSpeakingOrder = [...speakingOrderRef.current];

      participants.forEach((participant) => {
        // Check if participant is currently speaking
        const isSpeaking = participant.isSpeaking;

        // Enhanced speaking detection with multiple methods (lower sensitivity to reduce false positives)
        const isActuallySpeaking =
          isSpeaking ||
          (participant.audioLevel !== undefined && participant.audioLevel > SPEAKING_AUDIO_LEVEL_THRESHOLD) ||
          (participant.lastSpokeAt !== undefined && currentTime - (participant.lastSpokeAt as unknown as number) < SPEAKING_LAST_SPOKE_MS);

        if (isActuallySpeaking) {
          newSpeakingIds.add(participant.identity);
          
          if (nextSpeakingTimes.get(participant.identity) !== currentTime) {
            nextSpeakingTimes.set(participant.identity, currentTime);
            speakingTimesChanged = true;
          }

          // Update speaking order - move this participant to the front if they're speaking
          const existingIndex = nextSpeakingOrder.indexOf(participant.identity);
          if (existingIndex !== 0) { // If not already at the front
            if (existingIndex !== -1) {
              nextSpeakingOrder.splice(existingIndex, 1);
            }
            nextSpeakingOrder.unshift(participant.identity);
            speakingOrderChanged = true;
          }
        }
      });

      if (speakingTimesChanged) {
        lastSpeakingTimesRef.current = nextSpeakingTimes;
        setLastSpeakingTimes(nextSpeakingTimes);
      }

      if (speakingOrderChanged) {
        speakingOrderRef.current = nextSpeakingOrder;
        setSpeakingOrder(nextSpeakingOrder);
      }

      // Use functional state update to avoid dependency
      setSpeakingIds((currentSpeakingIds) => {
        if (!areSetsEqual(newSpeakingIds, currentSpeakingIds)) {
          return newSpeakingIds;
        }
        return currentSpeakingIds;
      });
    };

    // Update speaking status (200ms interval to reduce flicker, align with useSpeakingParticipants)
    const intervalId = setInterval(updateSpeaking, SPEAKING_UPDATE_INTERVAL_MS);
    updateSpeaking(); // Initial call

    return () => clearInterval(intervalId);
  }, [participants]);

  // Debug test for speaking animations
  useEffect(() => {
    const handleDebugSpeakingTest = () => {
      const localParticipant = participants.find((p) => p.isLocal);
      if (localParticipant) {
        setSpeakingIds(new Set([localParticipant.identity]));
        setTimeout(() => {
          setSpeakingIds(new Set());
        }, 3000);
      }
    };

    window.addEventListener('debug-speaking-test', handleDebugSpeakingTest);
    return () => window.removeEventListener('debug-speaking-test', handleDebugSpeakingTest);
  }, [participants]);

  // Instant proximity updates with immediate responsiveness
  useEffect(() => {
    // Don't process proximity during loading - this prevents showing all participants during initialization
    if (isLoading) {
      setInProximityIds(new Set()); // Clear all proximity during loading
      return;
    }

    // Hysteresis: require 3 consecutive misses before removing a participant.
    // This prevents boundary-flicker from permanently excluding someone.
    const MISS_THRESHOLD = 3; // At 50ms poll = 150ms before removal

    const updateProximity = () => {
      const game = gameRef.current;
      if (!game?.myPlayer) {
        setInProximityIds((prev) => (prev.size === 0 ? prev : new Set<string>()));
        return;
      }
      const newProximityIds = calculateProximity();

      // Apply hysteresis: don't remove anyone until they've missed N consecutive checks
      setInProximityIds((currentProximityIds) => {
        // Build the result set with hysteresis applied
        const resultSet = new Set(newProximityIds);

        // For each currently-visible participant not in the fresh set, track misses
        currentProximityIds.forEach((id) => {
          if (!newProximityIds.has(id)) {
            const missCount = (consecutiveMissCountRef.current.get(id) || 0) + 1;
            consecutiveMissCountRef.current.set(id, missCount);
            if (missCount < MISS_THRESHOLD) {
              // Not enough consecutive misses — keep them in proximity
              resultSet.add(id);
            }
          } else {
            // Reset miss counter for participants still in proximity
            consecutiveMissCountRef.current.delete(id);
          }
        });

        // Prune miss counters for participants we're no longer tracking
        consecutiveMissCountRef.current.forEach((_, id) => {
          if (!currentProximityIds.has(id) && !newProximityIds.has(id)) {
            consecutiveMissCountRef.current.delete(id);
          }
        });

        if (!areSetsEqual(resultSet, currentProximityIds)) {
          // Schedule event dispatch for next tick to avoid setState during render
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent(PLAYER_PROXIMITY_CHANGED, {
                detail: { inProximityIds: Array.from(resultSet) },
              })
            );
          }, 0);
          return resultSet;
        }

        return currentProximityIds;
      });
    };

    // Use faster interval when visible, slower when tab hidden to save CPU/memory.
    // SC-257 / TC-03: keep the hidden cadence at 1s (not 2s) so a colleague walking
    // up while we're working in another tab is picked up and their mic subscribed
    // within ~1s instead of up to 2s.
    const cleanup = setVisibilityAwareInterval(updateProximity, 50, 1000);

    // Initial calculation
    updateProximity();
    updateProximityImmediateRef.current = updateProximity;

    return () => {
      updateProximityImmediateRef.current = null;
      cleanup();
    };
  }, [calculateProximity, isLoading]);

  // Re-run proximity immediately when server VR roster / filter membership changes.
  useEffect(() => {
    const bumpProximity = () => {
      updateProximityImmediateRef.current?.();
    };
    phaserEvents.on(Event.VIRTUAL_ROOM_OCCUPANTS_UPDATED, bumpProximity);
    phaserEvents.on(Event.VIRTUAL_ROOM_FILTER_STATE_UPDATED, bumpProximity);
    return () => {
      phaserEvents.off(Event.VIRTUAL_ROOM_OCCUPANTS_UPDATED, bumpProximity);
      phaserEvents.off(Event.VIRTUAL_ROOM_FILTER_STATE_UPDATED, bumpProximity);
    };
  }, []);

  // Debug function to test instant proximity responsiveness
  const testInstantProximity = useCallback(() => {

    const game = gameRef.current;
    if (!game || !game.myPlayer) {
      return;
    }

    // Force immediate proximity calculation
    const newProximityIds = calculateProximity();

    // Proximity calculation complete — no per-participant logging in production
  }, [participants, calculateProximity]);

  // Expose test function globally
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).testInstantProximity = testInstantProximity;
    }
  }, [testInstantProximity]);

  return {
    inProximityIds,
    speakingIds,
    speakingOrder,
    lastSpeakingTimes,
  };
}; 